import * as amplitude from '@amplitude/analytics-browser';
import {plugin as engagementPlugin} from '@amplitude/engagement-browser';
import {useCallback, useEffect, useState} from 'react';

import {selectUser} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

const isE2eBuild = import.meta.env.VITE_E2E === 'true';
const includeTelemetry = !isE2eBuild && import.meta.env.VITE_DEPLOYMENT_ENV === 'production';
const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
const serverZone = import.meta.env.VITE_AMPLITUDE_SERVER_ZONE as 'EU' | 'US' | undefined;

let analyticsInstance: typeof amplitude | undefined;
let analyticsInitializationPromise: Promise<typeof amplitude | undefined> | undefined;
let engagementPluginRegistered = false;

function getAnalyticsOptions() {
  return serverZone ? {serverZone} : undefined;
}

export async function initializeAnalytics() {
  if (!includeTelemetry || !apiKey || typeof window === 'undefined') {
    return undefined;
  }

  if (analyticsInstance) {
    return analyticsInstance;
  }

  if (!analyticsInitializationPromise) {
    analyticsInitializationPromise = Promise.resolve().then(() => {
      if (!engagementPluginRegistered) {
        amplitude.add(engagementPlugin(getAnalyticsOptions()));
        engagementPluginRegistered = true;
      }

      amplitude.init(apiKey, getAnalyticsOptions());
      analyticsInstance = amplitude;

      return analyticsInstance;
    });
  }

  return analyticsInitializationPromise;
}

export function useAnalytics() {
  const [analytics, setAnalytics] = useState<typeof amplitude | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    void initializeAnalytics().then((initializedAnalytics) => {
      if (isMounted) {
        setAnalytics(initializedAnalytics);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return analytics;
}

export function useTrackEvent() {
  const user = useAppSelector(selectUser);
  const analytics = useAnalytics();

  return useCallback(
    (event: string, properties?: Record<string, any>, options?: amplitude.Types.EventOptions) => {
      if (analytics) {
        const groupId = user?.email.endsWith('maddox.ai') ? `${user?.customer}_internal` : user?.customer;

        analytics.track(
          event,
          {
            ...properties,
            groupId,
          },
          options,
        );
      }
    },
    [analytics, user],
  );
}
